import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing, type TemplateResult } from 'lit';
import { reprocessNotificationReply } from '../../features/notifications/api/reprocessNotificationReply.js';
import { subscribeRequestCorrespondence } from '../../features/notifications/api/subscribeRequestCorrespondence.js';
import type { CorrespondenceAnswer, CorrespondenceEntry } from '../../features/notifications/db/Correspondence.js';

/**
 * The mail traffic about this request — what the platform asked, what came back, and how each
 * answer was read.
 *
 * It sits next to "Nachvollziehen" for the same reason that tab exists: an automatic step that
 * changes an offer has to be inspectable afterwards. The extraction can be re-derived from its
 * introspection nodes; a model's reading of a free-text sentence cannot, so it is recorded at
 * the time and shown here — the passage it rested on, how it was understood, how sure the model
 * was, and what that did or did not change.
 */
const SELECTION_SET = {
  _id: 1,
  direction: 1,
  at: 1,
  subject: 1,
  counterpart: 1,
  questions: 1,
  bodyText: 1,
  unmatched: 1,
  unread: 1,
  reading: 1,
  replyMailID: 1,
  answers: {
    _id: 1,
    questionTitle: 1,
    positionNumber: 1,
    quote: 1,
    chosenOption: 1,
    understoodAs: 1,
    reasoning: 1,
    confidence: 1,
    outcome: 1,
    rejections: 1,
    status: 1,
    needsConfirmation: 1,
  },
} as const;

const formatDate = (date: Date | null | undefined) =>
  date ? `${date.toLocaleDateString()}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';

/**
 * A reading below the applying threshold is the interesting case, so it gets the loud colour:
 * that is the answer sitting there doing nothing until someone confirms it.
 */
function renderConfidence(answer: CorrespondenceAnswer): TemplateResult | typeof nothing {
  if (answer.confidence === null || answer.confidence === undefined) return nothing;
  const percent = Math.round(answer.confidence * 100);
  const low = answer.confidence < 0.8;
  return html`<span
    class="badge badge-xs ${low ? 'badge-warning' : 'badge-ghost'}"
    title=${low ? 'Unter der Schwelle — wurde nicht automatisch übernommen' : 'Sicher genug, automatisch übernommen'}
    >${percent}% sicher</span
  >`;
}

function renderAnswer(answer: CorrespondenceAnswer): TemplateResult {
  return html`
    <div class="rounded-lg border border-base-200 bg-base-100 p-3">
      <div class="flex items-start gap-2">
        ${answer.positionNumber
          ? html`<span class="badge badge-xs badge-ghost shrink-0 mt-0.5">Pos. ${answer.positionNumber}</span>`
          : nothing}
        <span class="text-[13px] font-medium flex-1 min-w-0">${answer.questionTitle}</span>
        ${renderConfidence(answer)}
      </div>

      ${answer.quote
        ? html`<div class="mt-2 border-l-2 border-base-300 pl-2.5 text-[12.5px] text-base-content/60 italic">
            „${answer.quote}"
          </div>`
        : nothing}

      ${answer.chosenOption
        ? html`<div class="mt-2 text-[12.5px] flex items-start gap-1.5">
            <x-icon class="text-sm text-base-content/40">check_box</x-icon>
            <span>Angebotene Antwort gewählt: <b>${answer.chosenOption}</b> — ohne Deutung übernommen.</span>
          </div>`
        : nothing}

      ${answer.understoodAs
        ? html`<div class="mt-2 text-[12.5px]">
            <div class="flex items-start gap-1.5">
              <x-icon class="text-sm text-info/70">psychology</x-icon>
              <span>Verstanden als: <b>${answer.understoodAs}</b></span>
            </div>
            ${answer.reasoning
              ? html`<div class="text-base-content/55 mt-0.5 pl-[22px]">${answer.reasoning}</div>`
              : nothing}
          </div>`
        : nothing}

      ${answer.outcome
        ? html`<div class="mt-2 text-[12.5px] flex items-start gap-1.5">
            <x-icon class="text-sm ${answer.needsConfirmation ? 'text-warning' : 'text-success/70'}">
              ${answer.needsConfirmation ? 'pending' : 'arrow_forward'}
            </x-icon>
            <span>${answer.outcome}</span>
          </div>`
        : nothing}

      ${answer.rejections?.length
        ? html`<div class="mt-2 rounded bg-error/5 px-2.5 py-1.5 text-[12.5px]">
            <div class="font-medium text-error/80">Nicht übernommen</div>
            <ul class="list-disc pl-4 text-base-content/60">
              ${answer.rejections.map(reason => html`<li>${reason}</li>`)}
            </ul>
          </div>`
        : nothing}
    </div>
  `;
}

function renderEntry(
  entry: CorrespondenceEntry,
  ctx: { busy: boolean; onReread: (replyMailID: string) => void },
): TemplateResult {
  const outgoing = entry.direction === 'out';
  return html`
    <div class="relative pl-6 pb-4 border-l border-base-200 last:border-l-transparent last:pb-0">
      <span
        class="absolute -left-[9px] top-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full
               ${outgoing ? 'bg-primary/10 text-primary' : 'bg-success/10 text-success'}"
      >
        <x-icon class="text-[13px]">${outgoing ? 'outgoing_mail' : 'mail'}</x-icon>
      </span>

      <div class="flex flex-wrap items-baseline gap-x-2">
        <span class="text-[13px] font-semibold">
          ${outgoing ? 'Rückfrage gestellt' : 'Antwort eingegangen'}
        </span>
        <span class="text-xs text-base-content/45">
          ${outgoing ? 'an' : 'von'} ${entry.counterpart ?? 'unbekannt'} · ${formatDate(entry.at)}
        </span>
      </div>
      ${entry.subject ? html`<div class="text-xs text-base-content/50 truncate">${entry.subject}</div>` : nothing}

      ${entry.questions?.length
        ? html`<ul class="mt-1.5 space-y-0.5 text-[12.5px] text-base-content/65">
            ${entry.questions.map(question => html`<li>${question}</li>`)}
          </ul>`
        : nothing}

      ${entry.bodyText
        ? html`<div class="mt-1.5 rounded-lg bg-base-200/50 px-3 py-2 text-[12.5px] whitespace-pre-line">
            ${entry.bodyText}
          </div>`
        : nothing}

      ${entry.answers?.length
        ? html`<div class="mt-2 space-y-2">${entry.answers.map(renderAnswer)}</div>`
        : nothing}

      ${entry.reading
        ? html`<div class="mt-2 flex items-center gap-2 rounded-lg border border-info/30 bg-info/5 px-3 py-2">
            <span class="loading loading-spinner loading-xs text-info"></span>
            <span class="text-[12.5px]">
              Antwort wird ausgewertet — sie wird gerade gegen die offenen Fragen gelesen.
            </span>
          </div>`
        : nothing}

      ${entry.unread
        ? html`<div class="mt-2 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2">
            <div class="text-[12.5px] font-medium">Aus dieser Antwort wurde nichts übernommen.</div>
            <div class="text-[12.5px] text-base-content/60 mt-0.5">
              Entweder war die Auswertung zum Zeitpunkt des Eingangs nicht erreichbar, oder sie konnte den Text
              keiner der offenen Fragen zuordnen.
            </div>
            <button
              class="btn btn-xs btn-outline mt-2"
              ?disabled=${ctx.busy}
              @click=${() => entry.replyMailID && ctx.onReread(entry.replyMailID)}
            >
              <x-icon class="text-sm">refresh</x-icon>Erneut auswerten
            </button>
          </div>`
        : nothing}

      ${entry.unmatched?.length
        ? html`<div class="mt-2 rounded-lg bg-warning/5 px-3 py-2 text-[12.5px]">
            <div class="font-medium text-warning-content/80">Keiner Frage zugeordnet</div>
            <ul class="list-disc pl-4 text-base-content/60">
              ${entry.unmatched.map(line => html`<li>${line}</li>`)}
            </ul>
          </div>`
        : nothing}
    </div>
  `;
}

export const RequestCorrespondence = stateful((props: { mailID: string }) => {
  const [busy, setBusy] = useState(false);

  // Live, and only keyed on the request: a reply arriving, being read, and the answers it
  // produced are three states of the same letter, and someone reading this tab is looking at
  // exactly the letter that is moving. Re-subscribing on anything else would blank the list
  // mid-read — the tab would empty itself at the moment it has the most to say.
  const { value: entries, loading } = useQuery(
    () => subscribeRequestCorrespondence(props.mailID)(SELECTION_SET),
    [props.mailID],
  );

  const reread = (replyMailID: string) => {
    setBusy(true);
    void reprocessNotificationReply(replyMailID)()
      .then(message => XSnackbar.show(message))
      .catch((err: Error) => XSnackbar.show('Auswertung fehlgeschlagen: ' + err.message))
      .finally(() => setBusy(false));
  };

  if (loading && !entries) return html`<div class="text-sm text-base-content/40">Wird geladen…</div>`;
  if (!entries?.length)
    return html`<div class="text-sm text-base-content/40">
      Zu dieser Anfrage wurde noch keine Rückfrage per Mail gestellt. Rückfragen gehen nur raus, wenn etwas die
      Übermittlung an SAP blockiert.
    </div>`;

  return html`<div class="pl-2">${entries.map(entry => renderEntry(entry, { busy, onReread: reread }))}</div>`;
});
